/**
 * Advertorial add-to-cart + sticky bar.
 *
 * Progressive enhancement over plain `/cart/add?id=...` links: intercepts
 * clicks on `[data-adv-atc]` elements and on any `<a href*="/cart/add">`,
 * adds the variant via /cart/add.js and announces the change with a
 * CartLinesUpdateEvent (no `sections` HTML in the payload → the cart drawer
 * re-renders itself and, having `auto-open` + action `add`, opens).
 * Without JS the links fall through to the native /cart/add GET → cart page.
 *
 * Also drives `[data-adv-sticky]` (sections/adv-sticky-atc.liquid): shown
 * after scrolling past `data-threshold` px, hidden while an offer grid
 * (.advoffer__grid) is on screen so the bar never covers the real offer.
 */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const BUSY_CLASS = 'adv-atc--busy';

function targetFrom(element) {
  const explicit = element.closest('[data-adv-atc]');
  if (explicit?.dataset.advAtc) {
    return {
      el: explicit,
      id: explicit.dataset.advAtc,
      quantity: parseInt(explicit.dataset.advQty || '1', 10) || 1,
    };
  }
  const link = element.closest('a[href*="/cart/add"]');
  if (link) {
    try {
      const url = new URL(link.href, window.location.origin);
      const id = url.searchParams.get('id');
      if (id) return { el: link, id, quantity: parseInt(url.searchParams.get('quantity') || '1', 10) || 1 };
    } catch {
      /* malformed href → let the browser handle it */
    }
  }
  return null;
}

async function addToCart(id, quantity) {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
  });
  if (!res.ok) throw new Error(`add.js ${res.status}`);
  await res.json();
  const cartRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!cartRes.ok) throw new Error(`cart.js ${cartRes.status}`);
  return cartRes.json();
}

function announceAdd(cart, id, quantity) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(id), quantity }],
    promise: deferred.promise,
  });
  document.dispatchEvent(event);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { source: 'adv-atc', itemCount: cart.item_count, items: cart.items },
  });
}

document.addEventListener('click', async (clickEvent) => {
  const target = targetFrom(clickEvent.target);
  if (!target) return;
  clickEvent.preventDefault();
  const { el, id, quantity } = target;
  if (el.classList.contains(BUSY_CLASS)) return;
  el.classList.add(BUSY_CLASS);
  el.setAttribute('aria-busy', 'true');
  try {
    const cart = await addToCart(id, quantity);
    announceAdd(cart, id, quantity);
  } catch (error) {
    console.warn('[adv-atc]', error);
    // AJAX path failed (bot protection, network...) → native fallback.
    const href = el.getAttribute('href');
    window.location.href = href && href.includes('/cart/add') ? href : `/cart/add?id=${id}&quantity=${quantity}&return_to=/cart`;
    return;
  } finally {
    el.classList.remove(BUSY_CLASS);
    el.removeAttribute('aria-busy');
  }
});

/* ---------------- sticky bar ---------------- */

function initSticky() {
  const bar = document.querySelector('[data-adv-sticky]');
  if (!bar) return;
  const threshold = parseInt(bar.dataset.threshold || '600', 10);
  let offerVisible = false;
  let pastThreshold = false;

  const apply = () => {
    bar.toggleAttribute('data-visible', pastThreshold && !offerVisible);
  };

  const onScroll = () => {
    const next = window.scrollY > threshold;
    if (next !== pastThreshold) {
      pastThreshold = next;
      apply();
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  const offer = document.querySelector('.advoffer__grid');
  if (offer && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        offerVisible = entries.some((entry) => entry.isIntersecting);
        apply();
      },
      { rootMargin: '0px 0px -10% 0px' }
    ).observe(offer);
  }

  // Hide the bar while the cart drawer is open (it overlaps the drawer footer).
  document.addEventListener(StandardEvents.cartLinesUpdate, () => {
    // After an add the drawer opens on top; nothing to do, kept for clarity.
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSticky, { once: true });
} else {
  initSticky();
}
